import { Theme } from '@emotion/react'
import { ThemeDocument } from '../../types.generated'
import { asText } from '@prismicio/client'
import styled from '@emotion/styled'
import { scrollstoryBackgroundColor } from '../components/atoms/Atoms'
import { RichTextField } from '@prismicio/types'

export const VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP = '2em'
export const VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE = '1.4em'

export const OVERLAY_TEXT_BOX_PADDING = '2em'
export const OVERLAY_TEXT_BOX_MAX_WIDTH = '450px'

export const GlobalThemeContainer = styled.div`
  ${scrollstoryBackgroundColor}

  ul, ol {
    list-style: revert;
  }

  * {
    overflow-wrap: break-word;
    hyphens: auto;

    color: black;
  }

  ${({ theme }) => `
    ${theme.fontFacesCss}
    ${theme.cssTheme}
  `}
`

// Serializes text to string and ensures CSS strings end with a newline.
// (newline is necessary to fix a bug: if the last line was a css comment, all
// later css statements would be ignored)
const mapCSS = (css: RichTextField): string => {
  if (!css) {return ''}
  return (asText(css) ?? '').trimEnd() + '\n'
}

export const mapTheme = (themeDocument: ThemeDocument): Theme => {
  const shouldUseFontFaces = themeDocument.data.font_faces_allowlist.some(
    (allowListEntry) =>
      allowListEntry.font_face_allow_url === null ||
      window.location.href.indexOf(allowListEntry.font_face_allow_url) > -1
  )

  return {
    backgroundColor: themeDocument.data.background_color ?? '',
    backgroundColorDark: themeDocument.data.background_color_dark ?? '',
    inlineElementAlign: themeDocument.data.inline_alignment || 'center',
    inlineTextMaxWidth: themeDocument.data.inline_text_max_width || '660px',
    inlineElementMaxWidth:
      themeDocument.data.inline_element_max_width || '979px',
    inlinePaddingLeftRightDesktop:
      themeDocument.data.inline_padding_leftRight_desktop || '20px',
    inlinePaddingLeftRightMobile:
      themeDocument.data.inline_padding_leftRight_mobile || '24px',
    cssTheme: mapCSS(themeDocument.data.css_theme),
    inlineCaptionCss: mapCSS(themeDocument.data.inline_caption_css),
    fontFacesCss: shouldUseFontFaces
      ? mapCSS(themeDocument.data.font_faces_css)
      : '',
    headerHeightPortrait: themeDocument.data.header_height_portrait ?? 0,
    headerHeightLandscape: themeDocument.data.header_height_landscape ?? 0,
    scrollOverMediaCoverContainerCss:
      mapCSS(themeDocument.data.scrollOverMedia_cover_container_css),
    scrollOverMediaCoverCss:
      mapCSS(themeDocument.data.scrollOverMedia_cover_css),
    scrollOverMediaCoverCaptionCss:
      mapCSS(themeDocument.data.scrollOverMedia_cover_caption_css),
    scrollOverMediaTextboxesCss:
      mapCSS(themeDocument.data.scrollOverMedia_textboxes_css),
    quoteContainerCss: mapCSS(themeDocument.data.quote_container_css),
    quoteContentCss: mapCSS(themeDocument.data.quote_content_css),
    quoteTextCss: mapCSS(themeDocument.data.quote_text_css),
    quoteCaptionContainerCss: mapCSS(themeDocument.data.quote_caption_container_css),
    quoteCaptionCss: mapCSS(themeDocument.data.quote_caption_css),
    foldoutContainerCss: mapCSS(themeDocument.data.foldout_container_css),
    foldoutTextCss: mapCSS(themeDocument.data.foldout_text_css),
    foldoutCaptionCss: mapCSS(themeDocument.data.foldout_caption_css),
    foldoutTitleCss: mapCSS(themeDocument.data.foldout_title_css),
    foldoutIcon: themeDocument.data.foldout_icon?.url ?? '',
    subtitlesCss: mapCSS(themeDocument.data.subtitles_css),
    mediaControlsContainerCss: mapCSS(themeDocument.data.media_controls_container),
    mediaControlsProgressBarUnfilled: mapCSS(themeDocument.data.media_controls_progress_bar_unfilled),
    mediaControlsProgressBarFilled: mapCSS(themeDocument.data.media_controls_progress_bar_filled),
    mediaControlsProgressBarKnob: mapCSS(themeDocument.data.media_controls_progress_bar_knob),
    mediaControlsScrollIndicator: mapCSS(themeDocument.data.media_controls_scroll_indicator),
    mediaControlsInlineVideoPlayButton: mapCSS(themeDocument.data.media_controls_inline_video_play_button),
    mediaControlsControlButton: mapCSS(themeDocument.data.media_controls_control_button),
    mediaControlsInlineAudioPlayerContainer: mapCSS(themeDocument.data.media_controls_inline_audio_player_container),
    mediaControlsInlineAudioPlayerPlayPauseButton: mapCSS(themeDocument.data.media_controls_inline_audio_player_play_pause_button),
    mediaControlsInlineAudioPlayerDescription: mapCSS(themeDocument.data.media_controls_inline_audio_player_description),
    mediaControlsInlineAudioPlayerDuration: mapCSS(themeDocument.data.media_controls_inline_audio_player_duration),
    mediaControlsInlineAudioPlayerLink: mapCSS(themeDocument.data.media_controls_inline_audio_player_link),
    mediaControlsInlineAudioPlayerProgressBarContainer: mapCSS(themeDocument.data.media_controls_inline_audio_player_progress_bar_container),
    mediaControlsInlineAudioPlayerProgressBar: mapCSS(themeDocument.data.media_controls_inline_audio_player_progress_bar),
    mediaControlsInlineAudioPlayerProgressBarFilled: mapCSS(themeDocument.data.media_controls_inline_audio_player_progress_bar_filled),
    mediaControlsInlineAudioPlayerProgressBarTimeDisplay: mapCSS(themeDocument.data.media_controls_inline_audio_player_progress_bar_time_display),
    mediaControlsInlineAudioPlayerProgressBarKnob: mapCSS(themeDocument.data.media_controls_inline_audio_player_progress_bar_knob),
    playIcon: themeDocument.data.play_icon?.url ?? '',
    pauseIcon: themeDocument.data.pause_icon?.url ?? '',
    muteIcon: themeDocument.data.mute_icon?.url ?? '',
    unmuteIcon: themeDocument.data.unmute_icon?.url ?? '',
    injectionJs: asText(themeDocument.data.injection_js) ?? '',
  }
}
